// @flow

import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";
import { formatAuthoredBy } from "@transformers/names";
import { Loader, View, Text } from "@components";
import { getRouteParams } from "@selectors";
import withErrorHandler from "@components/error";
import type { State } from "@store/reducers";
import { withClientContext, type PropsFromClientContext } from "@modules/context/components";
import { getEmployee } from "@modules/employee/selectors";
import type { EmployeeType } from "@modules/employee/types";
import { isLoading } from "@modules/shared/selectors";
import {
  withPermissions,
  PermissionEnum,
  type PropsFromPermissions,
} from "@modules/account/components";
import employeeActionCreators from "@modules/employee/actionCreator";
import { EmployeeAbsenceNotesSection } from "@modules/shared/components";

type PropsFromState = $Exact<{
  clientId: number,
  employeeId: number,
  employee: ?EmployeeType,
  isReportingPage: boolean,
  isLoading: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getEmployees: typeof employeeActionCreators.GetEmployees,
}>;

type Props = $Exact<{
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  ...PropsFromClientContext,
  ...PropsFromPermissions,
}>;

export class EmployeeNotes extends React.Component<Props> {
  componentDidMount() {
    this.props.getEmployees({
      clientId: this.props.clientId,
      filters: {
        id: this.props.employeeId,
      },
    });
  }

  render() {
    return (
      <View position="relative" gap="4">
        {this.props.isLoading && <Loader inline />}
        <Text className="message-box" text={this.props.t("EMPLOYEE_NOTES_TAB_INFO")} />
        <EmployeeAbsenceNotesSection
          employeeId={this.props.employeeId}
          clientId={this.props.clientId}
          reporting={this.props.isReportingPage}
        />
      </View>
    );
  }
}

const mapStateToProps = (state: State, ownProps): PropsFromState => {
  const routeParams = getRouteParams(ownProps);
  const employeeId = parseInt(routeParams.employeeId);
  const clientId = parseInt(routeParams.clientId, 10);
  const isReportingPage = (((ownProps || {}).location || {}).pathname || "").includes("reporting");

  return {
    clientId,
    employeeId,
    employee: getEmployee(employeeId)(state),
    isLoading: isLoading(state),
    isReportingPage,
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getEmployees: employeeActionCreators.GetEmployees,
};

export default compose(
  withErrorHandler(),
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
  withPermissions({ permissionId: PermissionEnum.ACCESS_REPORTING_EMPLOYEES }),
  withClientContext(),
)(EmployeeNotes);
